// The route tree. A new area adds its routes here (under the shell) and one line in src/nav.ts.
import { Outlet, createRootRoute, createRoute, createRouter, lazyRouteComponent, redirect, type AnyRoute, type RouteComponent } from "@tanstack/react-router";
import { IMPORT_KINDS, type ImportKind } from "@imagora/contracts";
import { validateCampaignsSearch } from "../features/ads/view";
import { RouteError, RouteNotFound, RoutePending } from "./route-states";

const rootRoute = createRootRoute({ component: Outlet, notFoundComponent: RouteNotFound });

/** Everything that needs a session. Pages for people without one, like signed approval links, go directly under the root. */
const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "shell",
  component: lazyRouteComponent(() => import("../layout/AppShell"), "AppShell"),
});

const homeRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/",
  component: lazyRouteComponent(() => import("../features/home/HomeScreen"), "HomeScreen"),
});

const queueRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "queue",
  validateSearch: (search: Record<string, unknown>): { id?: string } => (typeof search.id === "string" ? { id: search.id } : {}),
  component: lazyRouteComponent(() => import("../features/queue/QueueScreen"), "QueueScreen"),
});

const calendarRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "calendar",
  component: lazyRouteComponent(() => import("../features/calendar/CalendarScreen"), "CalendarScreen"),
});

const studioRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "studio",
  validateSearch: (search: Record<string, unknown>): { at?: string; run?: string; topic?: string } => ({
    ...(typeof search.at === "string" ? { at: search.at } : {}),
    ...(typeof search.run === "string" ? { run: search.run } : {}),
    ...(typeof search.topic === "string" ? { topic: search.topic } : {}),
  }),
  component: lazyRouteComponent(() => import("../features/studio/StudioScreen"), "StudioScreen"),
});

const libraryRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "library",
  component: lazyRouteComponent(() => import("../features/library/LibraryScreen"), "LibraryScreen"),
});

const visualEditorRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "visual-editor",
  validateSearch: (search: Record<string, unknown>): { creative?: string; asset?: string; template?: string } => ({
    ...(typeof search.creative === "string" ? { creative: search.creative } : {}),
    ...(typeof search.asset === "string" ? { asset: search.asset } : {}),
    ...(typeof search.template === "string" ? { template: search.template } : {}),
  }),
  component: lazyRouteComponent(() => import("../features/visual-editor/VisualEditorScreen"), "VisualEditorScreen"),
});

const deckEditorRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "visual-editor/deck",
  validateSearch: (search: Record<string, unknown>): { kind?: "carousel" | "document"; creative?: string } => ({
    ...(search.kind === "carousel" || search.kind === "document" ? { kind: search.kind } : {}),
    ...(typeof search.creative === "string" ? { creative: search.creative } : {}),
  }),
  component: lazyRouteComponent(() => import("../features/media/DeckEditorScreen"), "DeckEditorScreen"),
});

const videoEditorRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "visual-editor/video",
  validateSearch: (search: Record<string, unknown>): { creative?: string } => (typeof search.creative === "string" ? { creative: search.creative } : {}),
  component: lazyRouteComponent(() => import("../features/media/VideoEditorScreen"), "VideoEditorScreen"),
});

const channelsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "channels",
  component: lazyRouteComponent(() => import("../features/channels/ChannelsScreen"), "ChannelsScreen"),
});

/** The signed phone link: one named person, no session, no navigation around it. */
const publicApprovalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "approve/$token",
  component: lazyRouteComponent(() => import("./public-approval"), "PublicApprovalPage"),
});

const billingRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "billing",
  component: lazyRouteComponent(() => import("../features/billing/BillingScreen"), "BillingScreen"),
});

const aiUsageRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "ai-usage",
  component: lazyRouteComponent(() => import("../features/ai-usage/AiUsageScreen"), "AiUsageScreen"),
});

const agencyRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "agency",
  component: lazyRouteComponent(() => import("../features/agency/AgencyScreen"), "AgencyScreen"),
});

const accountDevicesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "account/devices",
  component: lazyRouteComponent(() => import("../features/signup/DevicesScreen"), "DevicesScreen"),
});

// s10-mcp-b: the AI assistants the person let into their workspaces, beside the devices.
const accountAssistantsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "account/assistants",
  component: lazyRouteComponent(() => import("../features/connect/AssistantConnections"), "AssistantsScreen"),
});

// s10-login-b: the way back from Google, Apple and Facebook, the account's sign-in methods, and Facebook's data deletion status.
const accountMethodsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "account/methods",
  component: lazyRouteComponent(() => import("../features/signup/MethodsScreen"), "MethodsScreen"),
});

const accountOAuthRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "account/oauth/$provider",
  component: lazyRouteComponent(() => import("../features/signup/OAuthReturnScreen"), "OAuthReturnScreen"),
});

const accountDeletionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "account/deletion",
  component: lazyRouteComponent(() => import("../features/signup/DeletionScreen"), "DeletionScreen"),
});

const welcomeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "welcome",
  component: lazyRouteComponent(() => import("../features/signup/WorkspaceScreen"), "WorkspaceScreen"),
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "login",
  component: lazyRouteComponent(() => import("../features/signup/LoginScreen"), "LoginScreen"),
});

// s10-mcp-a: an assistant asking to connect, sent here by the OAuth server.
const connectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "connect",
  validateSearch: (search: Record<string, unknown>): { request?: string; error?: string } => ({
    ...(typeof search.request === "string" ? { request: search.request } : {}),
    ...(typeof search.error === "string" ? { error: search.error } : {}),
  }),
  component: lazyRouteComponent(() => import("../features/connect/ConnectScreen"), "ConnectScreen"),
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "signup",
  component: lazyRouteComponent(() => import("../features/signup/SignupScreen"), "SignupScreen"),
});

const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "invite",
  component: lazyRouteComponent(() => import("../features/team/InviteScreen"), "InviteScreen"),
});

const openRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "open",
  component: lazyRouteComponent(() => import("../features/signup/OpenScreen"), "OpenScreen"),
});

const accountVerifyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "account/verify",
  component: lazyRouteComponent(() => import("../features/signup/VerifyScreen"), "VerifyScreen"),
});

/** The platform role's pages: their own door, session and layout, and nothing of a tenant's shell. */
const adminLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "admin/login",
  component: lazyRouteComponent(() => import("../features/admin/AdminLogin"), "AdminLogin"),
});

const adminVerifyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "admin/verify",
  component: lazyRouteComponent(() => import("../features/admin/AdminLogin"), "AdminVerify"),
});

const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "admin",
  component: lazyRouteComponent(() => import("../features/admin/AdminLayout"), "AdminLayout"),
});

const adminIndexRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/admin/hosts", replace: true });
  },
});

const adminHostsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "hosts",
  component: lazyRouteComponent(() => import("../features/admin/HostsTab"), "HostsTab"),
});

const adminSupportRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "support",
  component: lazyRouteComponent(() => import("../features/admin/support/SupportLayout"), "SupportLayout"),
});

const adminSupportIndexRoute = createRoute({
  getParentRoute: () => adminSupportRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/admin/support/inbox", replace: true });
  },
});

const adminSupportInboxRoute = createRoute({
  getParentRoute: () => adminSupportRoute,
  path: "inbox",
  component: lazyRouteComponent(() => import("../features/admin/support/InboxPage"), "InboxPage"),
});

const adminSupportThreadRoute = createRoute({
  getParentRoute: () => adminSupportRoute,
  path: "inbox/$id",
  component: lazyRouteComponent(() => import("../features/admin/support/ThreadPage"), "ThreadPage"),
});

const adminSupportDocumentsRoute = createRoute({
  getParentRoute: () => adminSupportRoute,
  path: "documents",
  component: lazyRouteComponent(() => import("../features/admin/support/DocumentsPage"), "DocumentsPage"),
});

const adminSupportProposalsRoute = createRoute({
  getParentRoute: () => adminSupportRoute,
  path: "proposals",
  component: lazyRouteComponent(() => import("../features/admin/support/ProposalsPage"), "ProposalsPage"),
});

const adminSupportRepliesRoute = createRoute({
  getParentRoute: () => adminSupportRoute,
  path: "replies",
  component: lazyRouteComponent(() => import("../features/admin/support/RepliesPage"), "RepliesPage"),
});

const adminSupportGroupRoute = createRoute({
  getParentRoute: () => adminSupportRoute,
  path: "group",
  component: lazyRouteComponent(() => import("../features/admin/support/GroupPage"), "GroupPage"),
});

const adminTenantsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "tenants",
  component: lazyRouteComponent(() => import("../features/admin/TenantsTab"), "TenantsTab"),
});

const adminUsageRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "usage",
  component: lazyRouteComponent(() => import("../features/admin/UsageTab"), "UsageTab"),
});

const adminReadinessRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "readiness",
  component: lazyRouteComponent(() => import("../features/admin/ReadinessTab"), "ReadinessTab"),
});

const adminAuditRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "audit",
  component: lazyRouteComponent(() => import("../features/admin/AuditTab"), "AuditTab"),
});

/** Where a platform's sign-in comes back to: a window of its own, with no session. */
const oauthDoneRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "oauth/done",
  validateSearch: (search: Record<string, unknown>): { oauth?: string; channel?: string } => ({
    ...(typeof search.oauth === "string" ? { oauth: search.oauth } : {}),
    ...(typeof search.channel === "string" ? { channel: search.channel } : {}),
  }),
  component: lazyRouteComponent(() => import("../features/channels/OAuthDonePage"), "OAuthDonePage"),
});

const weekPlanRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "week-plan",
  component: lazyRouteComponent(() => import("../features/week-plan/WeekPlanScreen"), "WeekPlanScreen"),
});

const themesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "themes",
  component: lazyRouteComponent(() => import("../features/themes/ThemesScreen"), "ThemesScreen"),
});

const themeDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "themes/$id",
  component: lazyRouteComponent(() => import("../features/themes/ThemeDetailScreen"), "ThemeDetailScreen"),
});

const postKindsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "post-kinds",
  component: lazyRouteComponent(() => import("../features/archetypes/ArchetypesScreen"), "ArchetypesScreen"),
});

const postTemplatesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "post-templates",
  component: lazyRouteComponent(() => import("../features/own-templates/OwnTemplatesScreen"), "OwnTemplatesScreen"),
});

const anglesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "angles",
  validateSearch: (search: Record<string, unknown>): { tab?: "angles" } => (search.tab === "angles" ? { tab: "angles" } : {}),
  component: lazyRouteComponent(() => import("../features/profiles/ProfilesAnglesScreen"), "ProfilesAnglesScreen"),
});

const topicsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "topics",
  component: lazyRouteComponent(() => import("../features/topics/TopicsScreen"), "TopicsScreen"),
});

const competitorsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "competitors",
  component: lazyRouteComponent(() => import("../features/competitors/CompetitorsScreen"), "CompetitorsScreen"),
});

const newsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "news",
  component: lazyRouteComponent(() => import("../features/news/NewsScreen"), "NewsScreen"),
});

const calendarEventsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "calendar-events",
  component: lazyRouteComponent(() => import("../features/calendar-events/CalendarEventsScreen"), "CalendarEventsScreen"),
});

const factsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "facts",
  component: lazyRouteComponent(() => import("../features/facts/FactsScreen"), "FactsScreen"),
});

const brandRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "brand",
  component: lazyRouteComponent(() => import("../features/brand/BrandScreen"), "BrandScreen"),
});

const knowledgeRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "knowledge",
  component: lazyRouteComponent(() => import("../features/knowledge/KnowledgeScreen"), "KnowledgeScreen"),
});

const adsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "ads",
  component: lazyRouteComponent(() => import("../features/ads/AdsLayout"), "AdsLayout"),
});
const adsIndexRoute = createRoute({
  getParentRoute: () => adsRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/ads/campaigns", replace: true });
  },
});
const adsCampaignsRoute = createRoute({
  getParentRoute: () => adsRoute,
  path: "campaigns",
  validateSearch: validateCampaignsSearch,
  component: lazyRouteComponent(() => import("../features/ads/CampaignsScreen"), "CampaignsScreen"),
});
const adsCellsRoute = createRoute({
  getParentRoute: () => adsRoute,
  path: "cells",
  component: lazyRouteComponent(() => import("../features/ads/CellsScreen"), "CellsScreen"),
});
const adsCellNewRoute = createRoute({
  getParentRoute: () => adsRoute,
  path: "cells/new",
  component: lazyRouteComponent(() => import("../features/ads/CellWizard"), "CellWizard"),
});
const adsCellRoute = createRoute({
  getParentRoute: () => adsRoute,
  path: "cells/$id",
  component: lazyRouteComponent(() => import("../features/ads/CellsScreen"), "CellDetail"),
});
const adsSearchRoute = createRoute({
  getParentRoute: () => adsRoute,
  path: "search",
  component: lazyRouteComponent(() => import("../features/ads/SearchScreen"), "SearchScreen"),
});
const adsSearchNewRoute = createRoute({
  getParentRoute: () => adsRoute,
  path: "search/new",
  component: lazyRouteComponent(() => import("../features/ads/SearchScreen"), "SearchNew"),
});
const adsSearchOneRoute = createRoute({
  getParentRoute: () => adsRoute,
  path: "search/$id",
  component: lazyRouteComponent(() => import("../features/ads/SearchScreen"), "SearchDetail"),
});
const adsPromoteRoute = createRoute({
  getParentRoute: () => adsRoute,
  path: "promote",
  component: lazyRouteComponent(() => import("../features/ads/PromoteScreen"), "PromoteScreen"),
});
const adsPromoteNewRoute = createRoute({
  getParentRoute: () => adsRoute,
  path: "promote/new",
  component: lazyRouteComponent(() => import("../features/ads/PromoteScreen"), "PromoteNew"),
});
const adsPromoteOneRoute = createRoute({
  getParentRoute: () => adsRoute,
  path: "promote/$id",
  component: lazyRouteComponent(() => import("../features/ads/PromoteScreen"), "PromoteDetail"),
});
const adsProductsRoute = createRoute({
  getParentRoute: () => adsRoute,
  path: "products",
  component: lazyRouteComponent(() => import("../features/ads/ProductAdsScreen"), "ProductAdsScreen"),
});
const adsProductsNewRoute = createRoute({
  getParentRoute: () => adsRoute,
  path: "products/new",
  component: lazyRouteComponent(() => import("../features/ads/ProductAdsScreen"), "ProductAdNew"),
});
const adsProductsOneRoute = createRoute({
  getParentRoute: () => adsRoute,
  path: "products/$id",
  component: lazyRouteComponent(() => import("../features/ads/ProductAdsScreen"), "ProductAdDetail"),
});
const adsAudiencesRoute = createRoute({
  getParentRoute: () => adsRoute,
  path: "audiences",
  component: lazyRouteComponent(() => import("../features/ads/AudiencesScreen"), "AudiencesScreen"),
});
const adsHoldsRoute = createRoute({
  getParentRoute: () => adsRoute,
  path: "holds",
  component: lazyRouteComponent(() => import("../features/ads/HoldsScreen"), "HoldsScreen"),
});
const adsGuardsRoute = createRoute({
  getParentRoute: () => adsRoute,
  path: "guards",
  component: lazyRouteComponent(() => import("../features/ads/GuardsScreen"), "GuardsScreen"),
});
const adsConnectionsRoute = createRoute({
  getParentRoute: () => adsRoute,
  path: "connections",
  component: lazyRouteComponent(() => import("../features/ads/ConnectionsScreen"), "ConnectionsScreen"),
});
const trackingRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "tracking",
  component: lazyRouteComponent(() => import("../features/tracking/TrackingLayout"), "TrackingLayout"),
});
const trackingIndexRoute = createRoute({
  getParentRoute: () => trackingRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/tracking/setup", replace: true });
  },
});
const trackingSetupRoute = createRoute({
  getParentRoute: () => trackingRoute,
  path: "setup",
  component: lazyRouteComponent(() => import("../features/tracking/SetupScreen"), "SetupScreen"),
});
const trackingConversionsRoute = createRoute({
  getParentRoute: () => trackingRoute,
  path: "conversions",
  component: lazyRouteComponent(() => import("../features/tracking/ConversionsScreen"), "ConversionsScreen"),
});

const analyticsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "analytics",
  component: lazyRouteComponent(() => import("../features/analytics/AnalyticsLayout"), "AnalyticsLayout"),
});

const analyticsIndexRoute = createRoute({
  getParentRoute: () => analyticsRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/analytics/funnel", replace: true });
  },
});

const analyticsFunnelRoute = createRoute({ getParentRoute: () => analyticsRoute, path: "funnel", component: lazyRouteComponent(() => import("../features/analytics/FunnelScreen"), "FunnelScreen") });
const analyticsCircuitRoute = createRoute({ getParentRoute: () => analyticsRoute, path: "circuit", component: lazyRouteComponent(() => import("../features/analytics/CircuitScreen"), "CircuitScreen") });
const analyticsPostsRoute = createRoute({ getParentRoute: () => analyticsRoute, path: "posts", component: lazyRouteComponent(() => import("../features/analytics/PostsScreen"), "PostsScreen") });
const analyticsScoresRoute = createRoute({ getParentRoute: () => analyticsRoute, path: "scores", component: lazyRouteComponent(() => import("../features/analytics/ScoresScreen"), "ScoresScreen") });
const analyticsHoursRoute = createRoute({ getParentRoute: () => analyticsRoute, path: "hours", component: lazyRouteComponent(() => import("../features/analytics/GoodHoursScreen"), "GoodHoursScreen") });
const analyticsFiguresRoute = createRoute({ getParentRoute: () => analyticsRoute, path: "figures", component: lazyRouteComponent(() => import("../features/analytics/FiguresScreen"), "FiguresScreen") });

const reportsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "reports",
  validateSearch: (search: Record<string, unknown>): { week?: string; agency?: string } => ({ ...(typeof search.week === "string" ? { week: search.week } : {}), ...(typeof search.agency === "string" ? { agency: search.agency } : {}) }),
  component: lazyRouteComponent(() => import("../features/reports/ReportsScreen"), "ReportsScreen"),
});

const linksRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "links",
  component: lazyRouteComponent(() => import("../features/links/LinksScreen"), "LinksScreen"),
});

const settingsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "settings",
  component: lazyRouteComponent(() => import("../features/settings/SettingsLayout"), "SettingsLayout"),
});

const settingsIndexRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/settings/readiness", replace: true });
  },
});

const readinessRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "readiness",
  component: lazyRouteComponent(() => import("../features/readiness/ReadinessScreen"), "ReadinessScreen"),
});

const liveRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "live",
  component: lazyRouteComponent(() => import("../features/live/LiveSwitchesScreen"), "LiveSwitchesScreen"),
});

const teamRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "team",
  component: lazyRouteComponent(() => import("../features/team/TeamScreen"), "TeamScreen"),
});

const notificationsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "notifications",
  component: lazyRouteComponent(() => import("../features/team/NotificationsScreen"), "NotificationsScreen"),
});

const aiKeysRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "ai",
  component: lazyRouteComponent(() => import("../features/ai-keys/AiKeysScreen"), "AiKeysScreen"),
});

// s10-keys-b
const smsProviderRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "sms",
  component: lazyRouteComponent(() => import("../features/sms-provider/SmsProviderScreen"), "SmsProviderScreen"),
});

const apiKeysRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "api-keys",
  component: lazyRouteComponent(() => import("../features/api-keys/ApiKeysScreen"), "ApiKeysScreen"),
});

const auditRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "audit",
  component: lazyRouteComponent(() => import("../features/audit/AuditScreen"), "AuditScreen"),
});

const profileRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "profile",
  component: lazyRouteComponent(() => import("../features/tenant/TenantProfileScreen"), "TenantProfileScreen"),
});

const leadsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "leads",
  validateSearch: (search: Record<string, unknown>): { view?: "pipeline" } => (search.view === "pipeline" ? { view: "pipeline" } : {}),
  component: lazyRouteComponent(() => import("../features/leads/LeadsScreen"), "LeadsScreen"),
});

const leadSequencesRoute = createRoute({ getParentRoute: () => shellRoute, path: "lead-sequences", component: lazyRouteComponent(() => import("../features/leads/SequencesScreen"), "SequencesScreen") });
const leadSequenceNewRoute = createRoute({ getParentRoute: () => shellRoute, path: "lead-sequences/new", component: lazyRouteComponent(() => import("../features/leads/SequenceEditorScreen"), "SequenceEditorScreen") });
const leadSequenceRoute = createRoute({ getParentRoute: () => shellRoute, path: "lead-sequences/$id", component: lazyRouteComponent(() => import("../features/leads/SequenceEditorScreen"), "SequenceEditorScreen") });

const leadSettingsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "leads/settings",
  validateSearch: (search: Record<string, unknown>): { tab?: string } => (typeof search.tab === "string" ? { tab: search.tab } : {}),
  component: lazyRouteComponent(() => import("../features/leads/PipelineSettingsScreen"), "PipelineSettingsScreen"),
});

const leadCardRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "leads/$id",
  component: lazyRouteComponent(() => import("../features/leads/LeadCardScreen"), "LeadCardScreen"),
});

const formsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "forms",
  validateSearch: (search: Record<string, unknown>): { view?: "landing" } => (search.view === "landing" ? { view: "landing" } : {}),
  component: lazyRouteComponent(() => import("../features/forms/FormsScreen"), "FormsScreen"),
});

const landingBuilderRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "forms/landing/$id",
  component: lazyRouteComponent(() => import("../features/forms/LandingBuilderScreen"), "LandingBuilderScreen"),
});

const formBuilderRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "forms/$id",
  component: lazyRouteComponent(() => import("../features/forms/FormBuilderScreen"), "FormBuilderScreen"),
});

const consentRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "consent",
  validateSearch: (search: Record<string, unknown>): { contact?: string; view?: string } => ({
    ...(typeof search.contact === "string" ? { contact: search.contact } : {}),
    ...(typeof search.view === "string" ? { view: search.view } : {}),
  }),
  component: lazyRouteComponent(() => import("../features/consent/ConsentScreen"), "ConsentScreen"),
});

const customizationRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "customization",
  component: lazyRouteComponent(() => import("../features/settings/CustomizationScreen"), "CustomizationScreen"),
});

const webhooksRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "webhooks",
  component: lazyRouteComponent(() => import("../features/settings/WebhooksScreen"), "WebhooksScreen"),
});

/** Inside the shell, so a mistyped address still has navigation around it. */
const shellNotFoundRoute = createRoute({ getParentRoute: () => shellRoute, path: "$", component: RouteNotFound });

const customersRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/customers",
  component: lazyRouteComponent(() => import("../features/customers/CustomersScreen"), "CustomersScreen"),
});

const customerCardRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/customers/$id",
  component: lazyRouteComponent(() => import("../features/customers/CustomerCardScreen"), "CustomerCardScreen"),
});

const segmentsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/segments",
  component: lazyRouteComponent(() => import("../features/segments/SegmentsScreen"), "SegmentsScreen"),
});

const segmentNewRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/segments/new",
  component: lazyRouteComponent(() => import("../features/segments/SegmentEditorScreen"), "SegmentEditorScreen"),
});

const segmentEditRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/segments/$id",
  component: lazyRouteComponent(() => import("../features/segments/SegmentEditorScreen"), "SegmentEditorScreen"),
});

const proposalsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/proposals",
  component: lazyRouteComponent(() => import("../features/proposals/ProposalsScreen"), "ProposalsScreen"),
});

const dataSourcesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/data-sources",
  component: lazyRouteComponent(() => import("../features/data-sources/DataSourcesScreen"), "DataSourcesScreen"),
});

const dataImportRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/data-sources/import",
  validateSearch: (search: Record<string, unknown>): { id?: string; kind?: ImportKind } => ({
    ...(typeof search["id"] === "string" ? { id: search["id"] } : {}),
    ...((IMPORT_KINDS as readonly unknown[]).includes(search["kind"]) ? { kind: search["kind"] as ImportKind } : {}),
  }),
  component: lazyRouteComponent(() => import("../features/imports/ImportScreen"), "ImportScreen"),
});

const templatesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/templates",
  component: lazyRouteComponent(() => import("../features/templates/TemplatesScreen"), "TemplatesScreen"),
});

const templateNewRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/templates/new",
  component: lazyRouteComponent(() => import("../features/templates/TemplateEditorScreen"), "TemplateEditorScreen"),
});

const templateEditRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/templates/$id",
  component: lazyRouteComponent(() => import("../features/templates/TemplateEditorScreen"), "TemplateEditorScreen"),
});

const campaignsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/campaigns",
  component: lazyRouteComponent(() => import("../features/campaigns/CampaignsScreen"), "CampaignsScreen"),
});

const campaignNewRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/campaigns/new",
  validateSearch: (search: Record<string, unknown>): { segment?: string } => (typeof search["segment"] === "string" ? { segment: search["segment"] } : {}),
  component: lazyRouteComponent(() => import("../features/campaigns/CampaignEditorScreen"), "CampaignEditorScreen"),
});

const campaignEditRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/campaigns/$id",
  component: lazyRouteComponent(() => import("../features/campaigns/CampaignEditorScreen"), "CampaignEditorScreen"),
});

const journeysRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/journeys",
  component: lazyRouteComponent(() => import("../features/journeys/JourneysScreen"), "JourneysScreen"),
});

const journeyNewRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/journeys/new",
  validateSearch: (search: Record<string, unknown>): { segment?: string } => (typeof search["segment"] === "string" ? { segment: search["segment"] } : {}),
  component: lazyRouteComponent(() => import("../features/journeys/JourneyEditorScreen"), "JourneyEditorScreen"),
});

const journeyEditRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/journeys/$id",
  component: lazyRouteComponent(() => import("../features/journeys/JourneyEditorScreen"), "JourneyEditorScreen"),
});

const referralRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/referral",
  component: lazyRouteComponent(() => import("../features/referral/ReferralScreen"), "ReferralScreen"),
});

const messagingSettingsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/messaging-settings",
  component: lazyRouteComponent(() => import("../features/messaging-settings/MessagingSettingsScreen"), "MessagingSettingsScreen"),
});

export interface RouterOptions {
  /** The component gallery, passed only by development builds. */
  gallery?: () => Promise<{ GalleryScreen: RouteComponent }>;
  /** The channel previews' gallery, passed only by development builds. */
  previews?: () => Promise<{ PreviewGallery: RouteComponent }>;
}

export function createAppRouter({ gallery, previews }: RouterOptions = {}) {
  const children: AnyRoute[] = [
    shellRoute.addChildren([
      homeRoute,
      queueRoute, calendarRoute, studioRoute, libraryRoute, visualEditorRoute, deckEditorRoute, videoEditorRoute, channelsRoute,
      weekPlanRoute, themesRoute, themeDetailRoute, topicsRoute, postKindsRoute, postTemplatesRoute, anglesRoute, competitorsRoute, newsRoute, calendarEventsRoute, factsRoute, brandRoute, knowledgeRoute,
      adsRoute.addChildren([adsIndexRoute, adsCampaignsRoute, adsCellsRoute, adsCellNewRoute, adsCellRoute, adsSearchRoute, adsSearchNewRoute, adsSearchOneRoute, adsPromoteRoute, adsPromoteNewRoute, adsPromoteOneRoute, adsProductsRoute, adsProductsNewRoute, adsProductsOneRoute, adsAudiencesRoute, adsHoldsRoute, adsGuardsRoute, adsConnectionsRoute]),
      trackingRoute.addChildren([trackingIndexRoute, trackingSetupRoute, trackingConversionsRoute]),
      leadsRoute, leadSettingsRoute, leadCardRoute, leadSequencesRoute, leadSequenceNewRoute, leadSequenceRoute, formsRoute, landingBuilderRoute, formBuilderRoute, consentRoute,
      analyticsRoute.addChildren([analyticsIndexRoute, analyticsFunnelRoute, analyticsCircuitRoute, analyticsPostsRoute, analyticsScoresRoute, analyticsHoursRoute, analyticsFiguresRoute]),
      reportsRoute,
      linksRoute,
      settingsRoute.addChildren([settingsIndexRoute, readinessRoute, customizationRoute, teamRoute, notificationsRoute, liveRoute, aiKeysRoute, smsProviderRoute, apiKeysRoute, webhooksRoute, auditRoute, profileRoute]),
      billingRoute,
      aiUsageRoute,
      agencyRoute,
      accountDevicesRoute,
      accountAssistantsRoute,
      // s10-login-b
      accountMethodsRoute,
      customersRoute, customerCardRoute, segmentsRoute, segmentNewRoute, segmentEditRoute, proposalsRoute, dataSourcesRoute, dataImportRoute, templatesRoute, templateNewRoute, templateEditRoute, campaignsRoute, campaignNewRoute, campaignEditRoute, journeysRoute, journeyNewRoute, journeyEditRoute, referralRoute, messagingSettingsRoute,
      shellNotFoundRoute,
    ]),
    publicApprovalRoute,
    loginRoute,
    // s10-mcp-a
    connectRoute,
    signupRoute,
    accountVerifyRoute,
    // s10-login-b
    accountOAuthRoute,
    accountDeletionRoute,
    welcomeRoute,
    openRoute,
    inviteRoute,
    adminLoginRoute,
    adminVerifyRoute,
    adminRoute.addChildren([adminIndexRoute, adminHostsRoute, adminTenantsRoute, adminUsageRoute, adminReadinessRoute, adminAuditRoute, adminSupportRoute.addChildren([adminSupportIndexRoute, adminSupportInboxRoute, adminSupportThreadRoute, adminSupportDocumentsRoute, adminSupportProposalsRoute, adminSupportRepliesRoute, adminSupportGroupRoute])]),
    oauthDoneRoute,
  ];
  if (gallery) {
    children.push(createRoute({ getParentRoute: () => rootRoute, path: "__gallery", component: lazyRouteComponent(gallery, "GalleryScreen") }));
  }
  if (previews) {
    children.push(createRoute({ getParentRoute: () => rootRoute, path: "dev/previews", component: lazyRouteComponent(previews, "PreviewGallery") }));
  }
  return createRouter({
    routeTree: rootRoute.addChildren(children),
    defaultPendingComponent: RoutePending,
    defaultErrorComponent: RouteError,
    defaultPendingMs: 200,
    defaultPendingMinMs: 0,
  });
}

export type AppRouter = ReturnType<typeof createAppRouter>;
